import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    DxSchedulerModule,
    DxSchedulerTypes,
} from 'devextreme-angular/ui/scheduler';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxToolbarModule } from 'devextreme-angular/ui/toolbar';
import { DxTooltipModule } from 'devextreme-angular/ui/tooltip';
import { DxTagBoxModule } from 'devextreme-angular';
import {
    ThemeService,
    CalendarAppointmentDto,
    AppointmentStatus,
    AppointmentsClient,
    GetCalendarAppointmentsQuery,
} from '@services';
import { CapitalCasePipe } from '../../../pipes/capital-case.pipe';
import { BehaviorSubject, map } from 'rxjs';
import { DxoToolbarComponent } from 'devextreme-angular/ui/nested';
import { AppointmentStatusPipe } from 'src/app/pipes/appointment-status.pipe';
import EnumUtils from 'src/app/shared/enum-utils';
import { Router } from '@angular/router';

class CalendarAppointment extends CalendarAppointmentDto {
    text: string;
    allDay: boolean;
    displayStatus?: string | null;
}

type FilterModel = {
    selectedStatuses: AppointmentStatus[];
};

@Component({
    selector: 'app-appointment-calendar',
    standalone: true,
    imports: [
        CommonModule,
        DxSchedulerModule,
        DxButtonModule,
        DxToolbarModule,
        DxTooltipModule,
        DxTagBoxModule,
        CapitalCasePipe,
        DxoToolbarComponent,
        AppointmentStatusPipe,
    ],
    templateUrl: './appointment-calendar.component.html',
})
export class AppointmentCalendarComponent implements OnInit {
    @Input() offset: number = 100;

    private router = inject(Router);
    private themeService = inject(ThemeService);
    private appointmentsClient = inject(AppointmentsClient);

    currentDate: Date = new Date();
    events$: BehaviorSubject<CalendarAppointment[]> = new BehaviorSubject<CalendarAppointment[]>([]);
    private rawData: CalendarAppointment[] = [];

    allStatuses = EnumUtils.getEnumOptions(AppointmentStatus);

    filterModel: FilterModel = {
        selectedStatuses: EnumUtils.getEnumOptions(AppointmentStatus).map((opt) => opt.value),
    };

    statusResources = [
        {
            fieldExpr: 'displayStatus',
            dataSource: [] as any[],
            label: 'Status',
        },
    ];

    protected readonly AppointmentStatus = AppointmentStatus;

    ngOnInit(): void {
        this.themeService.isDark$.subscribe(() => {
            this.initResourceColors();
        });

        this.loadSchedulerData();
    }

    onOptionChanged(e: any) {
        if (e.name === 'currentDate') {
            this.currentDate = e.value;
            this.loadSchedulerData();
        }
    }

    loadSchedulerData() {
        const { from, to } = this.getDateRange(this.currentDate);
        let query = new GetCalendarAppointmentsQuery({
            from,
            to,
        });
        this.appointmentsClient
            .getCalendarAppointments(query)
            .pipe(
                map((data: CalendarAppointmentDto[]) =>
                    data.map((event) => {
                        return {
                            ...event,
                            text: event.label,
                            allDay: false,
                            displayStatus: event.status,
                        } as CalendarAppointment;
                    }),
                ),
            )
            .subscribe((data) => {
                this.rawData = data;
                this.filterEvents();
            });
    }

    getDateRange(currentDate: Date) {
        const from = new Date(currentDate);
        const to = new Date(currentDate);

        from.setDate(1);
        to.setMonth(to.getMonth() + 1);
        to.setDate(0);

        return { from, to };
    }

    getAppointmentTextClass(data: CalendarAppointment) {
        const variant = this.getAppointmentStatusVariant(data.status);
        return variant ? `text-${variant}` : '';
    }

    getAppointmentMarkerClass(data: CalendarAppointment) {
        const variant = this.getAppointmentStatusVariant(data.status);
        return variant ? `bg-${variant}` : '';
    }

    private getAppointmentStatusVariant(
        status: AppointmentStatus | null | undefined,
    ): string {
        switch (status) {
            case AppointmentStatus.Draft:
                return 'secondary';
            case AppointmentStatus.UnderReview:
                return 'primary';
            case AppointmentStatus.Declined:
                return 'danger';
            case AppointmentStatus.NoShow:
                return 'danger';
            case AppointmentStatus.Approved:
                return 'success';
            case AppointmentStatus.CheckedIn:
                return 'primary';
            case AppointmentStatus.ShuntMovePending:
                return 'warning';
            case AppointmentStatus.ReadyToUnload:
                return 'info';
            case AppointmentStatus.Investigation:
                return 'warning';
            case AppointmentStatus.Unloading:
                return 'primary';
            case AppointmentStatus.TagsAsnPgiRequired:
                return 'warning';
            case AppointmentStatus.Refused:
                return 'danger';
            case AppointmentStatus.Completed:
                return 'success';
            default:
                return '';
        }
    }

    initResourceColors() {
        const getStyle = (name: string) =>
            getComputedStyle(document.body).getPropertyValue(name).trim();

        this.statusResources[0].dataSource = [
            {
                id: 'Draft',
                text: 'Draft',
                color: getStyle('--bs-secondary-bg-subtle'),
            },
            {
                id: 'UnderReview',
                text: 'Under Review',
                color: getStyle('--bs-primary-bg-subtle'),
            },
            {
                id: 'Declined',
                text: 'Declined',
                color: getStyle('--bs-danger-bg-subtle'),
            },
            {
                id: 'NoShow',
                text: 'No Show',
                color: getStyle('--bs-danger-bg-subtle'),
            },
            {
                id: 'Approved',
                text: 'Approved',
                color: getStyle('--bs-success-bg-subtle'),
            },
            {
                id: 'CheckedIn',
                text: 'Checked In',
                color: getStyle('--bs-primary-bg-subtle'),
            },
            {
                id: 'ShuntMovePending',
                text: 'Shunt Move Pending',
                color: getStyle('--bs-warning-bg-subtle'),
            },
            {
                id: 'ReadyToUnload',
                text: 'Ready To Unload',
                color: getStyle('--bs-info-bg-subtle'),
            },
            {
                id: 'Investigation',
                text: 'Investigation',
                color: getStyle('--bs-warning-bg-subtle'),
            },
            {
                id: 'Unloading',
                text: 'Unloading',
                color: getStyle('--bs-primary-bg-subtle'),
            },
            {
                id: 'TagsAsnPgiRequired',
                text: 'Tags/ASN/PGI Required',
                color: getStyle('--bs-warning-bg-subtle'),
            },
            {
                id: 'Refused',
                text: 'Refused',
                color: getStyle('--bs-danger-bg-subtle'),
            },
            {
                id: 'Completed',
                text: 'Completed',
                color: getStyle('--bs-success-bg-subtle'),
            },
        ];
    }

    protected getBadgeClass(status: AppointmentStatus): string {
        return EnumUtils.getAppointmentStatusBadgeClass(status);
    }

    getEventData = (data: CalendarAppointment) => data;

    onStatusFilterChanged(e: any) {
        this.filterModel.selectedStatuses = Array.isArray(e.value)
            ? e.value
            : [];
        this.filterEvents();
    }

    filterEvents() {
        const filtered = this.rawData.filter(
            (event) =>
                event.status === null ||
                this.filterModel.selectedStatuses.includes(event.status),
        );
        this.events$.next(filtered);
    }

    async onAppointmentDblClick(e: DxSchedulerTypes.AppointmentDblClickEvent) {
        await this.navigateToAppointment(e.appointmentData.number);
    }

    async onTooltipClick(number : string) {
        await this.navigateToAppointment(number);
    }

    private async navigateToAppointment(number: string) {
        return await this.router.navigate([`/inbound/appointments/details/${number}`]);
    }

    clearFilters = () => {
        this.filterModel.selectedStatuses = this.allStatuses.map(
            (opt) => opt.value,
        );
        this.filterEvents();
    };
}
